/**
 * Acceptable Use Policy — users must accept this text before using an LLM app.
 *
 * Enforced on the endpoint proxy (bdr-agent proxy/aup_gate.rs). Saving changed
 * text bumps the version, which re-prompts everyone. Acceptances are stored
 * server-side in a keyed hash chain; SMTP below is used to email receipts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './ui';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';

interface Policy {
  enabled: boolean;
  text: string;
  version: number;
  text_hash: string;
  apps: string[];
  email_copy_enabled: boolean;
}
interface Smtp { host: string; port: number; secure: boolean; user: string; pass: string; from: string }
interface Acceptance {
  id: string; accepted_at: string; os_user: string; app: string; hostname: string | null;
  policy_version: number; email: string | null;
}

const APPS = ['gemini', 'chatgpt', 'claude', 'deepseek', 'cursor', 'grok'];
const EMPTY: Policy = { enabled: false, text: '', version: 0, text_hash: '', apps: APPS, email_copy_enabled: true };
const EMPTY_SMTP: Smtp = { host: '', port: 587, secure: false, user: '', pass: '', from: '' };
const json = { 'Content-Type': 'application/json' };

const AupPanel: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canWrite = usePermission('update:policy');
  const [p, setP] = useState<Policy>(EMPTY);
  const [smtp, setSmtp] = useState<Smtp>(EMPTY_SMTP);
  const [rows, setRows] = useState<Acceptance[]>([]);
  const [chain, setChain] = useState<string>('');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, s, r, v] = await Promise.all([
        fetch(`${apiUrl}/admin/bdr/aup`), fetch(`${apiUrl}/admin/settings/smtp`),
        fetch(`${apiUrl}/admin/bdr/aup/acceptances`), fetch(`${apiUrl}/admin/bdr/aup/verify`),
      ]);
      if (a.ok) setP({ ...EMPTY, ...(await a.json()) });
      if (s.ok) setSmtp({ ...EMPTY_SMTP, ...(await s.json()) });
      if (r.ok) setRows(await r.json());
      if (v.ok) {
        const c = await v.json();
        setChain(c.ok ? `Chain intact (${c.checked} records)` : `CHAIN BROKEN at ${c.brokenAt}`);
      }
    } catch (e: any) {
      setMsg({ kind: 'error', text: `Could not load AUP settings: ${e.message}` });
    }
  }, [apiUrl]);

  useEffect(() => { void load(); }, [load]);

  const call = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ kind: 'ok', text: ok }); }
    catch (e: any) { setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  const savePolicy = () => call(async () => {
    const r = await fetch(`${apiUrl}/admin/bdr/aup`, { method: 'PUT', headers: json, body: JSON.stringify(p) });
    if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
    setP({ ...EMPTY, ...(await r.json()) });
  }, 'Saved. Endpoints pick this up within one policy poll; changed text re-prompts everyone.');

  const saveSmtp = () => call(async () => {
    const r = await fetch(`${apiUrl}/admin/settings/smtp`, { method: 'PUT', headers: json, body: JSON.stringify(smtp) });
    if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
    setSmtp({ ...EMPTY_SMTP, ...(await r.json()) });
  }, 'SMTP settings saved.');

  const testSmtp = () => call(async () => {
    const r = await fetch(`${apiUrl}/admin/settings/smtp/test`, { method: 'POST', headers: json, body: JSON.stringify({ to: testTo }) });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Test failed: HTTP ${r.status}`);
  }, `Test email sent to ${testTo}.`);

  const toggleApp = (a: string) =>
    setP({ ...p, apps: p.apps.includes(a) ? p.apps.filter((x) => x !== a) : [...p.apps, a] });

  const dis = !canWrite || busy;
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginTop: 12 };

  return (
    <>
      {msg && (
        <div className={`notice ${msg.kind === 'error' ? 'error' : ''}`}>
          <Icon name={msg.kind === 'error' ? 'warning' : 'checkCircle'} /> {msg.text}
        </div>
      )}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('components.aupPanel.acceptableUsePolicyVersion', { version: p.version || '—' })}</div>
        <div className="ent-card-body">
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={p.enabled} disabled={dis}
              onChange={(e) => setP({ ...p, enabled: e.target.checked })} /> {t('components.aupPanel.requireAcceptanceBeforeLLMUse')}
          </label>
          <label style={lbl}>{t('components.aupPanel.appsThatRequireAcceptance')}</label>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {APPS.map((a) => (
              <label key={a} style={{ fontSize: 13 }}>
                <input type="checkbox" checked={p.apps.includes(a)} disabled={dis} onChange={() => toggleApp(a)} /> {a}
              </label>
            ))}
          </div>
          <label style={lbl}>{t('components.aupPanel.policyText')}</label>
          <textarea className="ent-input" rows={16} value={p.text} disabled={dis}
            style={{ width: '100%', maxWidth: 760, fontSize: 13 }}
            placeholder={t('components.aupPanel.pasteTheFullAcceptableUsePolicy')}
            onChange={(e) => setP({ ...p, text: e.target.value })} />
          <label style={{ fontSize: 13, display: 'block', marginTop: 10 }}>
            <input type="checkbox" checked={p.email_copy_enabled} disabled={dis}
              onChange={(e) => setP({ ...p, email_copy_enabled: e.target.checked })} /> {t('components.aupPanel.emailACopyToTheAddress')}
          </label>
          {canWrite ? (
            <button className="ent-btn" style={{ marginTop: 16 }} disabled={busy} onClick={savePolicy}>
              {busy ? t('components.aupPanel.saving') : t('components.aupPanel.savePolicy')}
            </button>
          ) : (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-2)' }}>{t('components.aupPanel.readOnlyYouLackTheUpdate')}</div>
          )}
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('components.aupPanel.emailSMTP')}</div>
        <div className="ent-card-body">
          {([['host', t('components.aupPanel.host')], ['user', t('components.aupPanel.username')], ['from', t('components.aupPanel.fromAddress')]] as const).map(([k, l]) => (
            <div key={k}><label style={lbl}>{l}</label>
              <input className="ent-input" style={{ maxWidth: 320 }} value={smtp[k]} disabled={dis}
                onChange={(e) => setSmtp({ ...smtp, [k]: e.target.value })} /></div>
          ))}
          <label style={lbl}>{t('components.aupPanel.password')}</label>
          <input className="ent-input" type="password" style={{ maxWidth: 320 }} value={smtp.pass} disabled={dis}
            onChange={(e) => setSmtp({ ...smtp, pass: e.target.value })} />
          <label style={lbl}>{t('components.aupPanel.port')}</label>
          <input className="ent-input" type="number" style={{ maxWidth: 120 }} value={smtp.port} disabled={dis}
            onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} />
          <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
            <input type="checkbox" checked={smtp.secure} disabled={dis}
              onChange={(e) => setSmtp({ ...smtp, secure: e.target.checked })} /> {t('components.aupPanel.useTLSPort465')}
          </label>
          {canWrite && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="ent-btn" disabled={busy} onClick={saveSmtp}>{t('components.aupPanel.saveSMTP')}</button>
              <input className="ent-input" style={{ maxWidth: 240 }} placeholder={t('components.aupPanel.testRecipient')} value={testTo}
                onChange={(e) => setTestTo(e.target.value)} />
              <button className="ent-btn" disabled={busy || !testTo} onClick={testSmtp}>{t('components.aupPanel.sendTest')}</button>
            </div>
          )}
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head">{t('components.aupPanel.acceptances')} {chain && <span style={{ fontWeight: 400, fontSize: 12 }}>— {chain}</span>}</div>
        <div className="ent-card-body" style={{ overflowX: 'auto' }}>
          <a href={`${apiUrl}/admin/bdr/aup/acceptances?format=csv`} style={{ fontSize: 12 }}>{t('components.aupPanel.downloadCSV')}</a>
          <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
            <thead><tr><th align="left">{t('components.aupPanel.when')}</th><th align="left">{t('components.aupPanel.user')}</th><th align="left">{t('components.aupPanel.app')}</th>
              <th align="left">{t('components.aupPanel.device')}</th><th align="left">{t('components.aupPanel.ver')}</th><th align="left">{t('components.aupPanel.email')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}><td>{r.accepted_at.replace('T', ' ').slice(0, 19)}</td><td>{r.os_user}</td>
                  <td>{r.app}</td><td>{r.hostname}</td><td>{r.policy_version}</td><td>{r.email}</td></tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text-2)' }}>{t('components.aupPanel.noAcceptancesYet')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AupPanel;
